import React, { useEffect, useState } from "react";
import { Crown, X, Gift, Medal } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

export default function Champions() {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [claimFor, setClaimFor] = useState(null); // season string
  const [rsn, setRsn] = useState("");
  const [note, setNote] = useState("");

  const load = () => {
    setLoading(true);
    api
      .get("/seasons/champions")
      .then(({ data }) => setRows(data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (open) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const submitClaim = async (season) => {
    try {
      await api.post(`/seasons/${season}/claim-bond`, { rsn, note });
      toast.success("Bond claim submitted! We'll deliver it to your RSN.");
      setClaimFor(null);
      setRsn("");
      setNote("");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <>
      <button data-testid="open-champions" onClick={() => setOpen(true)} className="osrs-btn px-3 py-2 text-sm flex items-center gap-2">
        <Crown size={16} /> <span>Champions</span>
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4" onClick={() => setOpen(false)}>
          <div className="osrs-panel p-6 w-full max-w-lg max-h-[80vh] overflow-auto" onClick={(e) => e.stopPropagation()} data-testid="champions-modal">
            <div className="flex items-center justify-between mb-3">
              <h3 className="osrs-title text-2xl flex items-center gap-2"><Crown size={22} className="text-[#FFD700]" /> Champions</h3>
              <button onClick={() => setOpen(false)} className="osrs-btn px-2 py-1" data-testid="close-champions"><X size={16} /></button>
            </div>
            <div className="osrs-panel-parchment parchment-tex p-3 mb-4 flex items-center gap-2">
              <Gift size={18} className="text-[#7a1c1c]" />
              <p className="text-[#2a2113] text-xs font-bold">Each monthly #1 wins a free OSRS Bond! Seasons reset on the 1st.</p>
            </div>

            {loading ? (
              <p className="osrs-shadow text-[#E6A519] text-center py-8">Loading champions...</p>
            ) : rows.length === 0 ? (
              <p className="osrs-shadow text-[#E6A519] text-center py-8" data-testid="champions-empty">
                No champions crowned yet. The current month's winner will be immortalised here when the season ends!
              </p>
            ) : (
              <div className="flex flex-col gap-2" data-testid="champions-list">
                {rows.map((r) => (
                  <div key={r.season} data-testid={`champion-${r.season}`} className={`px-3 py-3 border-2 border-black ${r.is_me ? "bg-[#2f4a1c]" : "bg-[#241b10]"}`}>
                    <div className="flex items-center gap-3">
                      <Medal size={22} style={{ color: "#FFD700" }} />
                      <div className="flex-1 min-w-0">
                        <p className="osrs-shadow text-[#FFCF3F] font-bold">
                          {r.season} — {r.name} {r.is_me && <span className="text-[#00FF00] text-xs">(you)</span>}
                        </p>
                        <p className="osrs-num text-[#E6A519] text-xs">{r.total_points} pts · {r.verified} verified</p>
                      </div>
                      <span className={`osrs-num text-xs px-2 py-1 border border-black ${r.bond_status === "fulfilled" ? "text-[#00FF00]" : "text-[#FFD700]"}`} data-testid={`bond-status-${r.season}`}>
                        🎁 Bond: {r.bond_status}
                      </span>
                    </div>

                    {r.is_me && r.bond_status === "pending" && !r.claim && (
                      claimFor === r.season ? (
                        <div className="mt-3 space-y-2" data-testid="claim-form">
                          <input className="osrs-input w-full px-2 py-1" placeholder="Your OSRS username (RSN)" value={rsn} maxLength={12} onChange={(e) => setRsn(e.target.value)} data-testid="claim-rsn" />
                          <input className="osrs-input w-full px-2 py-1" placeholder="Note (optional)" value={note} maxLength={200} onChange={(e) => setNote(e.target.value)} data-testid="claim-note" />
                          <div className="flex gap-2">
                            <button className="osrs-btn osrs-btn-green px-3 py-1 text-xs" onClick={() => submitClaim(r.season)} disabled={!rsn.trim()} data-testid="submit-claim">Submit Claim</button>
                            <button className="osrs-btn px-3 py-1 text-xs" onClick={() => setClaimFor(null)}>Cancel</button>
                          </div>
                        </div>
                      ) : (
                        <button className="osrs-btn osrs-btn-green px-3 py-2 text-xs mt-2 flex items-center gap-1" onClick={() => setClaimFor(r.season)} data-testid={`claim-bond-${r.season}`}>
                          <Gift size={14} /> Claim your OSRS Bond
                        </button>
                      )
                    )}
                    {r.is_me && r.claim && (
                      <p className="osrs-shadow text-[#00FF00] text-xs mt-2" data-testid="claim-submitted">✔ Bond claimed for {r.claim.rsn} — pending delivery.</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
